import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { renderSegments, tagRanges } from '../../lib/renderSegments.jsx';
import { TAG_META } from '../../lib/spans.js';

/**
 * The caption: highlighted, taggable, and directly editable.
 *
 * ------------------------------------------------------------------------------
 * DO NOT introduce block-level children here.
 *
 * Character offsets are measured with `Range.toString()`, which counts text-node
 * content, not rendered layout. This must stay ONE `white-space: pre-wrap` element
 * containing only strings and `<mark>`. Splitting paragraphs into `<p>` elements would
 * shift every stored offset.
 * ------------------------------------------------------------------------------
 *
 * WHY THIS IS SAFE TO MAKE contentEditable
 *
 * The legacy tool put React-rendered `<mark>` children inside a contentEditable and let
 * React re-render them while the browser was editing the same nodes. The two fought:
 * caret jumps, dropped characters, duplicated nodes.
 *
 * Here React is frozen out for as long as the element has focus. On focus the rendered
 * children are captured and re-served by identity, so React reconciles them to a no-op
 * and never touches the DOM the browser is editing. Text is read back out and committed
 * on a typing pause and on blur; only then does React re-render, which also scrubs any
 * `<br>` or `<div>` the browser inserted along the way. Highlights therefore lag your
 * typing by a moment and snap back into place when you pause - which is the trade the
 * legacy design got wrong in the other direction.
 */
export const CaptionSurface = forwardRef(function CaptionSurface(
  {
    text,
    spans = [],
    addedSpans = [],
    extraRanges = [],
    activeSpanIndex = -1,
    onSpanClick,
    showTagLetters = false,
    editable = false,
    onCommit,
    onEditingChange,
    className = '',
    ...rest
  },
  ref,
) {
  const innerRef = useRef(null);
  const frozen = useRef(null);
  const [focused, setFocused] = useState(false);

  useImperativeHandle(ref, () => innerRef.current, []);

  const ranges = useMemo(
    () => [
      ...addedSpans.map((span) => ({
        start: span.start_char,
        end: span.end_char,
        className: 'human-added-text-highlight',
        title: 'Text you added',
      })),
      ...tagRanges(spans, { onSelect: onSpanClick, activeIndex: activeSpanIndex, meta: TAG_META }),
      ...extraRanges,
    ],
    [spans, addedSpans, extraRanges, activeSpanIndex, onSpanClick],
  );

  const content = useMemo(() => renderSegments(text, ranges), [text, ranges]);

  // While the caret is in here, keep handing React the very same elements so its diff
  // is a no-op and the browser owns the DOM uncontested.
  const rendered = focused && frozen.current ? frozen.current : content;
  if (!focused) frozen.current = content;

  const commit = useCallback(() => {
    const node = innerRef.current;
    if (!node || !editable) return;
    // innerText, not textContent: it renders a <br> the browser inserted as a newline.
    // (jsdom implements only textContent, hence the fallback.)
    const raw = node.innerText ?? node.textContent ?? '';
    // contentEditable leaves non-breaking spaces behind; normalise them back.
    const next = raw.replace(/\u00a0/g, ' ');
    if (next !== text) onCommit?.(next);
  }, [editable, onCommit, text]);

  // Commit on a pause as well as on blur, so a long edit is never one crash away from
  // being lost. Safe to do while focused precisely because of the freeze above.
  const idle = useRef(null);
  const scheduleCommit = useCallback(() => {
    clearTimeout(idle.current);
    idle.current = setTimeout(commit, 1200);
  }, [commit]);

  useEffect(() => () => clearTimeout(idle.current), []);
  useEffect(() => { onEditingChange?.(focused); }, [focused, onEditingChange]);

  if (!editable) {
    return (
      <p
        ref={innerRef}
        className={`caption-surface caption-body ${showTagLetters ? 'tag-letters' : ''} ${className}`}
        data-annotation-surface="true"
        {...rest}
      >
        {content}
      </p>
    );
  }

  return (
    <p
      ref={innerRef}
      className={`caption-surface caption-body editable ${focused ? 'is-editing' : ''} ${
        showTagLetters ? 'tag-letters' : ''
      } ${className}`}
      data-annotation-surface="true"
      contentEditable
      suppressContentEditableWarning
      spellCheck
      role="textbox"
      aria-multiline="true"
      aria-label="Caption - edit directly, or select text to tag it"
      onFocus={() => setFocused(true)}
      onBlur={() => {
        clearTimeout(idle.current);
        commit();
        setFocused(false);
      }}
      onInput={scheduleCommit}
      onKeyDown={(event) => {
        // Enter must insert a plain newline. Left to itself the browser splits the
        // paragraph into <div>s, which would break the single-element invariant above.
        if (event.key === 'Enter') {
          event.preventDefault();
          document.execCommand('insertText', false, '\n');
        }
        // Let the page shortcuts through only when they cannot be typing.
        if (event.key === 'Escape') innerRef.current?.blur();
      }}
      onPaste={(event) => {
        event.preventDefault();
        const plain = event.clipboardData?.getData('text/plain') || '';
        document.execCommand('insertText', false, plain);
      }}
      {...rest}
    >
      {rendered}
    </p>
  );
});
